import { JetView } from 'webix-jet'; // Importing JetView from webix-jet
import { devConfig } from 'config/config'; // Importing development configuration
import { buildDocumentFileName, downloadDataUri, getDocumentFile, showDocumentFile } from 'helpers/registeredDocumentFile'; // Shared document file helpers

/**
 * Class representing the table of the Consultation Documents view.
 * @extends JetView
 */
export default class TableConsultationDocumentsView extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const canView = devConfig.getPermAction('consultationDocumentsView', 'Add'); // View file permission
        const canDownload = devConfig.getPermAction('consultationDocumentsDownload', 'Add'); // Download file permission

        // Actions column only when at least one of its icons is permitted
        const actionsColumns = canView || canDownload ? [actionsColumn()] : [];
        function actionsColumn() {
            return {
                id: 'rating', // Actions column, ignored on export
                header: _('Acciones'), // Column header
                width: 100, // Column width
                tooltip: false, // Each icon has its own webix_tooltip
                template: function () {
                    // Icons for view and download file, without acceptance; each one depends on its permission
                    let icons = '';
                    if (canView) {
                        icons +=
                            "<span webix_tooltip='" +
                            _('Visualizar archivo') +
                            "' class='webix_icon wxi-file icon-file-text viewDocumentFile prm-consultationDocumentsView' style='cursor:pointer;'></span>";
                    }
                    if (canDownload) {
                        icons +=
                            "<span webix_tooltip='" +
                            _('Descargar') +
                            "' class='webix_icon wxi-download downloadDocumentFile prm-consultationDocumentsDownload' style='cursor:pointer;'></span>";
                    }
                    return icons;
                },
            };
        }

        return {
            view: 'datatable', // DataTable component
            id: 'consultationDocumentsTable', // ID for the datatable
            css: 'webix_header_border webix_data_border webix_footer_border', // CSS classes for styling
            select: true, // Enable row selection
            tooltip: true, // Enables the webix_tooltip of the action icons
            columns: [
                ...actionsColumns,
                {
                    id: 'Name', // Column ID
                    header: _('Nombre del documento'), // Column header
                    fillspace: 2, // Takes twice the free space of the other columns
                    minWidth: 250, // Minimum column width
                    sort: 'string', // Sorting type
                },
                {
                    id: 'Type', // Column ID
                    header: _('Tipo de documento'), // Column header
                    fillspace: 1, // Takes the free space
                    minWidth: 200, // Minimum column width
                    sort: 'string', // Sorting type
                },
                {
                    id: 'Access', // Column ID, shown as it comes from the resource
                    header: _('Tipo de acceso'), // Column header
                    fillspace: 1, // Takes the free space
                    minWidth: 150, // Minimum column width
                    sort: 'string', // Sorting type
                },
                {
                    id: 'Date', // Column ID
                    header: _('Fecha de publicación'), // Column header
                    minWidth: 150, // Minimum column width
                    sort: 'string', // Sorting type
                    template: function (obj) {
                        // Only the date of "Y-m-d H:i:s"
                        return obj.Date ? String(obj.Date).split(' ')[0] : '';
                    },
                },
            ],
            onClick: {
                // Event handlers for click events
                viewDocumentFile: function (e, id) {
                    // Shared "Visualizar archivo" window with Id, Version and Name of the row; download with this section's permission, without audit
                    showDocumentFile(this.getItem(id.row), _, canDownload);
                },
                downloadDocumentFile: function (e, id) {
                    // Same resource as view file, without preview
                    const item = this.getItem(id.row); // Getting the clicked row item
                    getDocumentFile(
                        item,
                        function (url, extension) {
                            downloadDataUri(url, buildDocumentFileName(item.Name, extension)); // Document name with its extension
                        },
                        function () {
                            // Request error, backend error or empty file
                            webix.message(_('No se pudo obtener el archivo'));
                        },
                    );
                },
            },
            on: {
                // Shows or hides "No hay datos" after every load
                onAfterLoad: function () {
                    if (this.count()) {
                        this.hideOverlay(); // Data received
                    } else {
                        this.showOverlay(
                            "<div class='no-data' style='margin:75px; font-size:18px;'>" + _('No hay datos') + '</div>',
                        ); // Show no data overlay
                    }
                },
            },
            loadahead: 0, // Load ahead configuration
            datafetch: devConfig.getDefaultDataFetch(), // Fetch data configuration
            pager: 'consultationDocumentsPager', // Pager ID
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     * @param {Object} grid - The datatable instance.
     */
    init(grid) {
        webix.extend(grid, webix.OverlayBox); // Overlay for "No hay datos"
        var url = devConfig.geturlApi() + 'RegisteredDocument/getDocumentsProfile'; // API URL for fetching documents, same request format as GetDocuments

        // Adjusts total_count when fewer rows than the page size arrive (last block),
        // avoiding extra requests that render repeated records.
        grid.data.attachEvent('onParse', function (driver, response) {
            if (!response || !webix.isArray(response.data) || response.total_count === undefined) {
                return;
            }
            var pos = parseInt(response.pos, 10) || 0;
            var received = response.data.length;
            if (received < grid.data.$pagesize || (parseInt(response.total_count, 10) || 0) < pos + received) {
                response.total_count = pos + received;
            }
        });

        // Builds the query string from the filter values, same parameters as GetDocuments
        function buildFilter(filterData) {
            var params = ''; // Filter string
            Object.keys(filterData || {}).forEach(function (key) {
                var value = filterData[key];
                if (value === '' || value === null || value === undefined || typeof value === 'object') {
                    return; // Empty filters are not sent
                }
                if (key === 'Datepicker') {
                    var date = String(value).split(' - '); // Date range "Y-m-d - Y-m-d"
                    params += '&dateFrom=' + encodeURIComponent(date[0]); // Add dateFrom to filter string
                    params += '&dateTo=' + encodeURIComponent(date[1] || date[0]); // Add dateTo to filter string
                } else {
                    params += '&' + key + '=' + encodeURIComponent(value); // Add other filters to string
                }
            });
            return params;
        }

        // Reloads the datatable from the first page with the given filters
        function load(filter) {
            grid.clearAll(); // Clear the datatable
            grid.load(url + '?start=0&count=' + grid.data.$pagesize + filter); // Load data with filters
        }

        // Queried only from Consultar, same as the other sections
        this.on(this.app, 'consultationDocuments:filter', (filterData) => {
            load(buildFilter(filterData));
        });
    }
}

// FILE DOCUMENTED
